import type { RuntimeState } from '../domain/runtime.ts';
import { createRuntime } from '../domain/runtime.ts';
import { RuntimeLoadRefusal, inspectRuntimeRecord } from './runtimeCompatibility.ts';
import type { StoragePort } from './storagePort.ts';
import { WriteQueue } from './writeQueue.ts';

/**
 * Saves and restores a crawler's runtime state: active Buffs and Debuffs,
 * cooldowns, and the encounter if one is in progress.
 *
 * All of it survives closing the application, so a Fatigued crawler is still
 * Fatigued tomorrow and a 30-hour cooldown keeps running. Ending an encounter
 * does NOT delete this file; it only clears the encounter inside it.
 */
export class RuntimeRepository {
  private readonly storage: StoragePort;
  private readonly now: () => string;
  private readonly queue: WriteQueue;

  constructor(
    storage: StoragePort,
    now: () => string = () => new Date().toISOString(),
    queue: WriteQueue = new WriteQueue(),
  ) {
    this.storage = storage;
    this.now = now;
    this.queue = queue;
  }

  /**
   * The saved runtime state, or a fresh one when the crawler has never had any.
   *
   * "OrStart" APPLIES TO ABSENCE ONLY. If `readRuntime` returns null this
   * crawler has no runtime state and starting one is correct. If a record
   * EXISTS but this build cannot safely understand it, this throws a
   * `RuntimeLoadRefusal` and leaves the record exactly where it is.
   *
   * It used to return a blank runtime in both cases. The blank one then looked
   * authoritative to everything downstream, so the next save wrote it over real
   * persisted state - active Effects, cooldowns, elapsed time, an open fight -
   * that had only failed to parse. Failing closed costs a reload; failing open
   * costs the player their crawler's condition.
   *
   * Nothing here writes, deletes or repairs. `inspectRuntimeRecord` is pure and
   * is the same authority `CheckpointCommitter` uses on a journal's runtime leg.
   */
  async loadOrStart(characterId: string): Promise<RuntimeState> {
    const raw = await this.storage.readRuntime(characterId);
    if (raw === null) return createRuntime(characterId, this.now());

    const inspected = inspectRuntimeRecord(raw, characterId);
    if (!inspected.ok) throw new RuntimeLoadRefusal(inspected.kind, inspected.message);
    return inspected.runtime;
  }

  /** The record exactly as `save` would write it, without writing it. */
  stamp(runtime: RuntimeState): RuntimeState {
    return { ...runtime, updatedAt: this.now() };
  }

  async save(runtime: RuntimeState): Promise<RuntimeState> {
    const stamped = this.stamp(runtime);
    return this.queue.run(`runtime:${runtime.characterId}`, async () => {
      await this.storage.writeRuntime(stamped.characterId, stamped);
      return stamped;
    });
  }

  /** Wipes everything: only for deleting a crawler, never for ending a fight. */
  async clear(characterId: string): Promise<void> {
    await this.queue.run(`runtime:${characterId}`, () => this.storage.deleteRuntime(characterId));
  }

  drain(): Promise<void> {
    return this.queue.drain();
  }
}
